// Phase G4-A — buildDecisionInput: assemble AdaptationDecisionInput.
//
// This is the real work behind "decideNextActivity({...})". It pulls
// the kid's recent attempts from child_attempts and assembles all 12
// fields the engine's decideNextActivity consumes, using the Phase
// C/D/E builders:
//   - buildAttemptWindow   (Phase C)
//   - evaluateTransferEvidence + evaluateDelayedReview (Phase D)
//   - computeSkillMasteryState (Phase C, composes the two above)
//   - detectSameErrorLoop  (Phase E — returns the FULL MisconceptionSignal)
//   - deriveWalkbackSkillIds (Phase E)
//
// Content safety is read from the curriculum content reader: if the
// skill has ≥1 launch_safe item for independent practice, the engine
// may advance/review on it; if not, currentContentSafety routes the
// engine to its priority-1 safe fallback (the fail-closed behavior
// that gated the whole app before the G4 slice).

import type {
  AdaptationDecisionInput,
  AgeBand,
  ContentSafetyState,
  ProgressionState,
} from "@/adaptation/types";
import { AGE_BAND_ORDER } from "@/adaptation/types";
import { buildAttemptWindow } from "@/adaptation/attemptWindow";
import { computeSkillMasteryState } from "@/adaptation/masteryEvidence";
import { evaluateTransferEvidence, transferServingPathExists } from "@/adaptation/transferGating";
import { evaluateDelayedReview } from "@/adaptation/spacedReview";
import { detectSameErrorLoop, deriveWalkbackSkillIds } from "@/adaptation/misconceptionRouting";
import { getContentForMicroSkill } from "@/curriculum/readers/contentReader";
import learningProgressions from "@/curriculum/data/manifests/learningProgressions.json" with { type: "json" };
import { ageBandForSkill, domainForSkill } from "./skillMeta";
import { primaryPackForSkill } from "./packRouting";
import { fetchRecentAttempts, fetchTransferAttemptsForSkillOrThrow } from "./attemptLogger";
import { loadLocalAttemptsForSkill, loadLocalTaggedTransferAttempts, mergeAttempts } from "./localAttemptStore";

interface ProgressionRow {
  microSkillId: string;
  prerequisites: string[];
  unlocks: string[];
  primaryChannel: string;
  forbidsChannelPropagation: string[];
}

const PROGRESSIONS = learningProgressions as ProgressionRow[];
const PROG_BY_ID: Record<string, ProgressionRow> = (() => {
  const m: Record<string, ProgressionRow> = {};
  for (const p of PROGRESSIONS) m[p.microSkillId] = p;
  return m;
})();

/** Build the ContentSafetyState for a skill by checking whether the
 * fail-closed content reader returns ≥1 launch_safe item for
 * independent practice. No launch_safe content → the engine's
 * priority-1 safe-fallback fires (correct fail-closed behavior).
 *
 * MA-632: the pack is resolved via the shared, skill-keyed
 * primaryPackForSkill (packRouting.ts) — the SAME routing decision
 * useEngineQuestions.ts's pullItems and perSkillPoolValidator.ts use.
 * This used to be a THIRD, domain-keyed copy with no PACK_OVERRIDE at
 * all, so any skill reachable only through the override (ten of them,
 * measured) was asked about the WRONG pack here and fail-closed to
 * priority=1:safety_fallback even though it had real launch_safe
 * content in its real pack. See docs/build/specs/
 * MA-632-pack-routing-single-source.md. */
export function readContentSafety(skillId: string): ContentSafetyState {
  const pack = primaryPackForSkill(skillId);
  const safeItems = getContentForMicroSkill(skillId, pack, {
    audience: "independent_practice",
  });
  const hasLaunchSafe = safeItems.length > 0;
  const contentFile = `src/curriculum/data/content/${pack}.json`;

  if (hasLaunchSafe) {
    return {
      contentFile,
      contentId: (safeItems[0] as { id?: string }).id ?? null,
      launchStatus: "launch_safe",
      expertReviewNeeded: false,
      validatorPassed: true,
      independentPracticeAllowed: true,
      rawChildFacingLLMBlocked: true,
      safeForSelectedDecision: true,
    };
  }
  // Fail-closed: no launch_safe content → exclude from independent
  // practice. The engine routes to priority-1 review/safe-fallback.
  return {
    contentFile,
    contentId: null,
    launchStatus: "exclude_independent_practice",
    expertReviewNeeded: true,
    validatorPassed: true,
    independentPracticeAllowed: false,
    rawChildFacingLLMBlocked: true,
    safeForSelectedDecision: false,
  };
}

/** Build the ProgressionState from learningProgressions.json + the
 * kid's mastery context. nextFormalSkillTooEarly is computed by
 * comparing the next skill's age band to the kid's current band:
 * if the next skill is more than one band ahead, it's too early. */
export function buildProgressionState(
  skillId: string,
  currentAgeBand: AgeBand,
): ProgressionState {
  const entry = PROG_BY_ID[skillId];
  const nextFormalSkillId = entry?.unlocks?.[0] ?? null;
  const nextFormalSkillAgeBand = nextFormalSkillId
    ? ageBandForSkill(nextFormalSkillId)
    : null;

  // REVIEW FIX: derive from the single ordered band table (bands-as-data,
  // #186) instead of a hardcoded list that missed the grade-2 bands
  // "7.0-7.5"/"7.5+" — which made nextFormalSkillTooEarly silently dead for
  // every grade-2 skill (indexOf returned -1).
  const BANDS = AGE_BAND_ORDER;
  const curIdx = BANDS.indexOf(currentAgeBand);
  const nextIdx = nextFormalSkillAgeBand ? BANDS.indexOf(nextFormalSkillAgeBand) : -1;
  // Too early if the next formal skill is ≥2 bands above the kid's
  // current band. (Within +1 band is normal progression.)
  const nextFormalSkillTooEarly = nextIdx >= 0 && nextIdx - curIdx >= 2;

  return {
    progressionId: skillId,
    ageBand: currentAgeBand,
    currentSkillId: skillId,
    prerequisiteSkillIds: entry?.prerequisites ?? [],
    // Prereq mastery isn't re-checked here (the kid reached this skill
    // through placement/progression); treated as met. Phase H can add
    // a strict prereq-mastery gate if needed.
    prerequisitesMet: true,
    nextFormalSkillId,
    nextFormalSkillAgeBand,
    nextFormalSkillTooEarly,
    walkbackSkillIds: deriveWalkbackSkillIds(skillId, PROGRESSIONS as never),
    accelerationEligible: false, // engine computes via evaluateAccelerationEligibility
    compactReviewEligible: false,
    enrichmentInsteadSignals: [],
  };
}

/** Pure core: assemble AdaptationDecisionInput from an explicit
 * attempts array (no DB). buildDecisionInput delegates here after
 * fetching; the G4-E integration test calls this directly with
 * synthetic attempts so it exercises the real content-safety + engine
 * routing on the slice without needing Supabase. */
export function buildDecisionInputFromAttempts(
  childId: string,
  skillId: string,
  attempts: import("@/adaptation/types").ChildAttempt[],
): AdaptationDecisionInput {
  // 1b. Skill-scope ONCE, here, before any evidence builder sees the array.
  //     sessionAttemptsRef in Mixed/Foundation interleaves multiple skillIds
  //     (spaced review, cross-strand slots); none of the builders below
  //     filter by skillId themselves (evaluateTransferEvidence filters by
  //     transfer-task tag only, not skillId; masteryEvidence/misconception
  //     routing just slice(-N) the tail), so an unscoped array silently lets
  //     another skill's attempts into this skill's mastery window and
  //     same-error-loop detection (MA-381 item d).
  const skillAttempts = attempts.filter((a) => a.skillId === skillId);

  // 2. Transfer + delayed-review evidence (Phase D). computeSkillMastery
  //    needs both, so build them first.
  const transferAttempts = skillAttempts.filter(
    (a) => a.isTransferTask || a.transferTaskId !== null,
  );
  const transferEvidence = evaluateTransferEvidence(skillId, transferAttempts);
  const delayedReviewEvidence = evaluateDelayedReview(skillId, skillAttempts);

  // 3. AttemptWindow (Phase C).
  const attemptWindow = buildAttemptWindow(skillAttempts, childId, skillId);

  // 4. SkillMasteryState (Phase C, composes 2+3).
  const masteryState = computeSkillMasteryState(
    skillId,
    skillAttempts,
    transferEvidence,
    delayedReviewEvidence,
  );

  // 5. MisconceptionSignal — detectSameErrorLoop returns the FULL signal.
  const misconceptionSignal = detectSameErrorLoop(skillAttempts);

  // 6. ProgressionState.
  const ageBand = ageBandForSkill(skillId);
  const progressionState = buildProgressionState(skillId, ageBand);

  // 7. Content safety: current + candidate-next.
  const currentContentSafety = readContentSafety(skillId);
  const candidateNextContentSafety = progressionState.nextFormalSkillId
    ? readContentSafety(progressionState.nextFormalSkillId)
    : null;

  return {
    childId,
    ageBand,
    domain: domainForSkill(skillId),
    currentSkillId: skillId,
    attemptWindow,
    masteryState,
    transferEvidence,
    delayedReviewEvidence,
    misconceptionSignal,
    progressionState,
    currentContentSafety,
    candidateNextContentSafety,
    parentSummaryRequired: true,
  };
}

/** Assemble the full AdaptationDecisionInput for a (child, skill).
 * Reads attempts from child_attempts, runs the Phase C/D/E builders,
 * and reads content safety for the current + candidate-next skills. */
export async function buildDecisionInput(
  childId: string,
  skillId: string,
  // MA-303b RED round 2 (S2/S3/S6) — optional injectable client, forwarded
  // to both fetches below. Default omitted = both fetches fall back to
  // their own real-Supabase default, so every existing call site
  // (buildDecisionInput(childId, skillId), no third arg) is unaffected.
  // Exists so a test can exercise THIS function's real wiring — not a
  // re-implementation of it — against a stub data source.
  client?: Parameters<typeof fetchRecentAttempts>[3],
): Promise<AdaptationDecisionInput> {
  // 1. Recent attempts (chronological) for this skill. ENGINE-LOCAL-FIRST:
  //    merge the local mirror so the decision sees offline history too.
  //    (childId here is the canonical id when linked, else the local id —
  //    useTodaysMission passes `kidId ?? localChildId`.)
  const cloudAttempts = client
    ? await fetchRecentAttempts(childId, skillId, 50, client)
    : await fetchRecentAttempts(childId, skillId);
  let attempts = mergeAttempts(
    cloudAttempts,
    loadLocalAttemptsForSkill(childId, skillId),
  );
  // MA-303b RED round 1 (R1), corrected round 2 (S1) — fetchRecentAttempts
  // is a bounded window (limit 50 for this skill). A tagged transfer
  // attempt is rare (~1 per session) and can scroll out of that window
  // after ordinary practice, silently flipping D2's `required` back to
  // false with no pass ever recorded. Merge in the tagged-transfer history
  // (real `is_transfer_task` column filter — no schema change) via the
  // SAME `mergeAttempts` dedup helper already used above, only for
  // registered skills (the fetch is a no-op elsewhere, but gate it anyway
  // to avoid an unnecessary query on every non-registry skill).
  if (transferServingPathExists(skillId)) {
    // S1(a) — fail AS A WHOLE, mirroring fetchRecentAttempts' own
    // fail-to-`[]` contract on this SAME call path. The round-1 version
    // swallowed a tagged-fetch error to `[]` and quietly kept the bounded
    // half of the evidence, which is exactly the "regains Mastered" defect
    // this fetch exists to close — a transient RLS/network miss reopened
    // it with nothing but a console.error. On error, discard everything
    // this call was about to assert mastery from (not just the tagged
    // half), same as a `fetchRecentAttempts` failure already does.
    try {
      const transferAttempts = client
        ? await fetchTransferAttemptsForSkillOrThrow(childId, skillId, 2000, client)
        : await fetchTransferAttemptsForSkillOrThrow(childId, skillId);
      attempts = mergeAttempts(attempts, transferAttempts);
    } catch (error) {
      console.error(
        "[buildDecisionInput] tagged-transfer fetch failed — discarding this call's attempts entirely (fail-closed, matching fetchRecentAttempts' own contract):",
        error,
      );
      attempts = [];
    }
    // S1(b) — guest/local-only/offline profiles have no cloud row to fetch
    // at all. loadLocalAttemptsForSkill above is bounded (limit 50) exactly
    // like the cloud window this fetch exists to escape, so merge in the
    // FULL local tagged history (filtered first, never windowed first) —
    // an additive, dedup-safe merge that runs regardless of the cloud
    // outcome above (a linked child gets both; a local-only child gets
    // just this, instead of nothing).
    attempts = mergeAttempts(attempts, loadLocalTaggedTransferAttempts(childId, skillId));
  }
  return buildDecisionInputFromAttempts(childId, skillId, attempts);
}
